/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : Invalid Date                                      *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-03-06 14:23:24                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Components
  * Styles
  * Moment config
  * PlanningCalendar: display the planning calendar section
    * onSelectTimeSlot: function to call when a time slot is selected
    * onSelectEvent: function to call when an event is selected
    * startAccessor: get the start date of an event
    * endAccessor: get the end date of an event
    * titleAccessor: get the title of an event
    * hourRangeFormat: format the hour range
    * getEventColor: get the color of an event
*/

/* Imports */
import { Calendar, DateRange, momentLocalizer, NavigateAction, SlotInfo, View } from "react-big-calendar";
import moment from "moment";
import "moment/locale/ca";
import "moment/locale/de";
import "moment/locale/es";
import "moment/locale/fr";
import "moment/locale/it";
import "moment/locale/lb";
import "moment/locale/nl";
import "moment/locale/nl-be";
import "moment/locale/pl";
import "moment/locale/pt";
import "moment/locale/ro";
import "moment/dist/locale/ca";
import "moment/dist/locale/de";
import "moment/dist/locale/es";
import "moment/dist/locale/fr";
import "moment/dist/locale/it";
import "moment/dist/locale/lb";
import "moment/dist/locale/nl";
import "moment/dist/locale/nl-be";
import "moment/dist/locale/pl";
import "moment/dist/locale/pt";
import "moment/dist/locale/ro";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "react-big-calendar/lib/css/react-big-calendar.css";
/***/

/* Services */
import { color } from "../../../../services/colors/colors";
/***/

/* Components */
import Event from "./planning-components/Event";
/***/

/* Styles */
import "./PlanningCalendar.scss";
import { startOfMonth } from "../../../../services/utils/utils";
import CalendarToolBar from "./planning-components/CalendarToolBar";
import Header from "./planning-components/Header";
import { lastHoveredDayEnd } from "./slotSelection";
/***/

const SUPPORTED_CALENDAR_LOCALES: Record<string, string> = {
  ca: "ca",
  de: "de",
  en: "en",
  es: "es",
  fr: "fr",
  it: "it",
  lb: "lb",
  "lb-lu": "lb",
  nl: "nl",
  "nl-be": "nl-be",
  pl: "pl",
  pt: "pt",
  ro: "ro"
};

export const resolveCalendarLocale = (rawLocale: string | null | undefined): string => {
  if (!rawLocale) return "en";
  const normalized = rawLocale.trim().replace(/_/g, "-").toLowerCase();
  if (SUPPORTED_CALENDAR_LOCALES[normalized]) {
    return SUPPORTED_CALENDAR_LOCALES[normalized];
  }
  const base = normalized.split("-")[0];
  return SUPPORTED_CALENDAR_LOCALES[base] ?? "en";
};

const capitalizeFirstLetter = (value: string): string => {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const configureMomentLocale = (locale: string) => {
  moment.locale(locale);
  if (moment.localeData(locale)) {
    const months = Array.from({ length: 12 }, (_, i) =>
      capitalizeFirstLetter(moment.utc([2026, i, 15]).locale(locale).format("MMMM"))
    );
    const monthsShort = Array.from({ length: 12 }, (_, i) =>
      capitalizeFirstLetter(moment.utc([2026, i, 15]).locale(locale).format("MMM"))
    );
    const weekdays = Array.from({ length: 7 }, (_, i) =>
      capitalizeFirstLetter(moment.utc([2026, 0, 4 + i]).locale(locale).format("dddd"))
    );
    const weekdaysShort = Array.from({ length: 7 }, (_, i) =>
      capitalizeFirstLetter(moment.utc([2026, 0, 4 + i]).locale(locale).format("ddd"))
    );
    const weekdaysMin = Array.from({ length: 7 }, (_, i) =>
      capitalizeFirstLetter(moment.utc([2026, 0, 4 + i]).locale(locale).format("dd"))
    );

    moment.updateLocale(locale, {
      months,
      monthsShort,
      weekdays,
      weekdaysShort,
      weekdaysMin,
      week: {
        dow: 1,
        doy: 1
      }
    });
  }
};

const createLocalizer = () => momentLocalizer(moment);
/***/

/**
* PlanningCalendar: display the planning calendar section
* @param times - the list of times to display
* @param onTimeClick - the function to call when a time is clicked
* @param onEventClick - the function to call when an event is clicked
* @param className - additional class name
*/
export default function PlanningCalendar ({
  times,
  loading,
  onPlanningNavigate,
  onTimeClick,
  onEventClick,
  unscheduledWorkordersLength, 
  unasignedWorkordersLength, 
  drawerType, 
  setDrawerType,
  filterDrawerComponent,
  filterBadgesComponent,
  className
}: {
  times: EasyTech.Time[],
  loading: boolean,
  onPlanningNavigate?: (newDate: Date, view: View) => void,
  onTimeClick?: (info: {start: Date, end: Date}) => void,
  onEventClick?: (e: EasyTech.Time) => void,
  unscheduledWorkordersLength: number, 
  unasignedWorkordersLength: number, 
  drawerType: string | null, 
  setDrawerType: (type: string | null) => void,
  filterDrawerComponent: JSX.Element,
  filterBadgesComponent: JSX.Element,
  className?: string
}) {
  const { i18n } = useTranslation();
  const [timeState, setTimeState] = useState(new Date(new Date().setMinutes(0, 0, 0)));
  const [actualDate, setActualDateState] = useState(new Date(new Date().setMinutes(0, 0, 0)));
  const [currentView, setCurrentView] = useState<View>("week");

  const hasCompletedRealDates = (event: EasyTech.Time) => {
    return Boolean(event.workOrder?.time?.start && event.workOrder?.time?.end);
  };

  const resolveLocale = (preferredLng?: string | null): string => {
    if (preferredLng) {
      return resolveCalendarLocale(preferredLng);
    }
    const stored = typeof window !== "undefined" ? window.localStorage?.getItem("i18nextLng") : null;
    const i18nLng = i18n?.resolvedLanguage || i18n?.language;
    const candidate = (i18nLng && i18nLng !== "en" ? i18nLng : stored || i18nLng) || "en";
    return resolveCalendarLocale(candidate);
  };

  const [calendarLocale, setCalendarLocale] = useState<string>(() => {
    const locale = resolveLocale();
    configureMomentLocale(locale);
    return locale;
  });

  useEffect(() => {
    const applyLocale = (preferredLng?: string | null) => {
      const locale = resolveLocale(preferredLng);
      configureMomentLocale(locale);
      setCalendarLocale(locale);
    };

    applyLocale(i18n?.resolvedLanguage || i18n?.language);

    const handleLanguageChanged = (lng: string) => {
      applyLocale(lng);
    };
    const handleStorageChange = () => {
      applyLocale(typeof window !== "undefined" ? window.localStorage?.getItem("i18nextLng") : null);
    };

    i18n?.on?.("languageChanged", handleLanguageChanged);
    if (typeof window !== "undefined") {
      window.addEventListener("storage", handleStorageChange);
    }

    return () => {
      i18n?.off?.("languageChanged", handleLanguageChanged);
      if (typeof window !== "undefined") {
        window.removeEventListener("storage", handleStorageChange);
      }
    };
  }, [i18n, i18n?.language, i18n?.resolvedLanguage]);

  const localizer = useMemo(() => {
    configureMomentLocale(calendarLocale);
    return createLocalizer();
  }, [calendarLocale]);

  const calendarMessages = useMemo(() => {
    switch (calendarLocale) {
    case "fr":
      return {
        date: "Date",
        time: "Heure",
        event: "Événement",
        allDay: "Toute la journée",
        week: "Semaine",
        work_week: "Semaine ouvrée",
        day: "Jour",
        month: "Mois",
        previous: "Précédent",
        next: "Suivant",
        yesterday: "Hier",
        tomorrow: "Demain",
        today: "Aujourd'hui",
        agenda: "Agenda",
        noEventsInRange: "Aucun événement sur cette période",
        showMore: (total: number) => `+ ${total} de plus`
      };
    case "nl":
    case "nl-be":
      return {
        date: "Datum",
        time: "Tijd",
        event: "Evenement",
        allDay: "Hele dag",
        week: "Week",
        work_week: "Werkweek",
        day: "Dag",
        month: "Maand",
        previous: "Vorige",
        next: "Volgende",
        yesterday: "Gisteren",
        tomorrow: "Morgen",
        today: "Vandaag",
        agenda: "Agenda",
        noEventsInRange: "Geen evenementen in deze periode",
        showMore: (total: number) => `+ ${total} extra`
      };
    case "it":
      return {
        date: "Data",
        time: "Ora",
        event: "Evento",
        allDay: "Tutto il giorno",
        week: "Settimana",
        work_week: "Settimana lavorativa",
        day: "Giorno",
        month: "Mese",
        previous: "Precedente",
        next: "Successivo",
        yesterday: "Ieri",
        tomorrow: "Domani",
        today: "Oggi",
        agenda: "Agenda",
        noEventsInRange: "Nessun evento in questo intervallo",
        showMore: (total: number) => `+ altri ${total}`
      };
    case "de":
      return {
        date: "Datum",
        time: "Zeit",
        event: "Ereignis",
        allDay: "Ganztägig",
        week: "Woche",
        work_week: "Arbeitswoche",
        day: "Tag",
        month: "Monat",
        previous: "Zurück",
        next: "Weiter",
        yesterday: "Gestern",
        tomorrow: "Morgen",
        today: "Heute",
        agenda: "Agenda",
        noEventsInRange: "Keine Ereignisse in diesem Zeitraum",
        showMore: (total: number) => `+ ${total} weitere`
      };
    case "es":
      return {
        date: "Fecha",
        time: "Hora",
        event: "Evento",
        allDay: "Todo el día",
        week: "Semana",
        work_week: "Semana laboral",
        day: "Día",
        month: "Mes",
        previous: "Anterior",
        next: "Siguiente",
        yesterday: "Ayer",
        tomorrow: "Mañana",
        today: "Hoy",
        agenda: "Agenda",
        noEventsInRange: "No hay eventos en este rango",
        showMore: (total: number) => `+ ${total} más`
      };
    case "ca":
      return {
        date: "Data",
        time: "Hora",
        event: "Esdeveniment",
        allDay: "Tot el dia",
        week: "Setmana",
        work_week: "Setmana laboral",
        day: "Dia",
        month: "Mes",
        previous: "Anterior",
        next: "Següent",
        yesterday: "Ahir",
        tomorrow: "Demà",
        today: "Avui",
        agenda: "Agenda",
        noEventsInRange: "No hi ha esdeveniments en aquest rang",
        showMore: (total: number) => `+ ${total} més`
      };
    case "pl":
      return {
        date: "Data",
        time: "Czas",
        event: "Wydarzenie",
        allDay: "Cały dzień",
        week: "Tydzień",
        work_week: "Tydzień roboczy",
        day: "Dzień",
        month: "Miesiąc",
        previous: "Poprzedni",
        next: "Następny",
        yesterday: "Wczoraj",
        tomorrow: "Jutro",
        today: "Dzisiaj",
        agenda: "Agenda",
        noEventsInRange: "Brak wydarzeń w tym zakresie",
        showMore: (total: number) => `+ ${total} więcej`
      };
    case "ro":
      return {
        date: "Dată",
        time: "Oră",
        event: "Eveniment",
        allDay: "Toată ziua",
        week: "Săptămână",
        work_week: "Săptămână de lucru",
        day: "Zi",
        month: "Lună",
        previous: "Înapoi",
        next: "Înainte",
        yesterday: "Ieri",
        tomorrow: "Mâine",
        today: "Astăzi",
        agenda: "Agendă",
        noEventsInRange: "Niciun eveniment în acest interval",
        showMore: (total: number) => `+ încă ${total}`
      };
    case "lb":
      return {
        date: "Datum",
        time: "Zäit",
        event: "Evenement",
        allDay: "De ganzen Dag",
        week: "Woch",
        work_week: "Aarbechtswoch",
        day: "Dag",
        month: "Mount",
        previous: "Zréck",
        next: "Weider",
        yesterday: "Gëschter",
        tomorrow: "Muer",
        today: "Haut",
        agenda: "Agenda",
        noEventsInRange: "Keng Evenementer an dësem Zäitraum",
        showMore: (total: number) => `+ ${total} méi`
      };
    case "pt":
      return {
        date: "Data",
        time: "Hora",
        event: "Evento",
        allDay: "Dia inteiro",
        week: "Semana",
        work_week: "Semana de trabalho",
        day: "Dia",
        month: "Mês",
        previous: "Anterior",
        next: "Seguinte",
        yesterday: "Ontem",
        tomorrow: "Amanhã",
        today: "Hoje",
        agenda: "Agenda",
        noEventsInRange: "Sem eventos neste período",
        showMore: (total: number) => `+ ${total} mais`
      };
    default:
      return {
        date: "Date",
        time: "Time",
        event: "Event",
        allDay: "All day",
        week: "Week",
        work_week: "Work week",
        day: "Day",
        month: "Month",
        previous: "Back",
        next: "Next",
        yesterday: "Yesterday",
        tomorrow: "Tomorrow",
        today: "Today",
        agenda: "Agenda",
        noEventsInRange: "No events in this range",
        showMore: (total: number) => `+ ${total} more`
      };
    }
  }, [calendarLocale]);

  /**
  * onNavigate: function to call when navigate in planning
  * @param newDate - the slot information 
  * @param view - the slot information 
  * @param action - the slot information 
  */
  const onNavigate = (newDate: Date, view: View, action: NavigateAction) => {
    setActualDateState(newDate);
    setCurrentView(view);
  };
  /***/

  /**
  * onView: function to call when navigate in planning
  * @param view - the slot information 
  */
  const onView = (view: View) => {
    setCurrentView(view);
    setActualDateState(actualDate);
  };
  /***/

  /**
  * onSelectTimeSlot: function to call when a time slot is selected
  * @param slotInfo - the slot information 
  */
  const onSelectTimeSlot = (slotInfo: SlotInfo) => {
    if(onTimeClick) onTimeClick({
      start: slotInfo.start,
      end: lastHoveredDayEnd(slotInfo.end)
    });
    setTimeState(new Date(moment(slotInfo.start).format("x")));
  };
  /***/

  /**
  * onSelectEvent: function to call when an event is selected
  * @param event - event item 
  */
  const onSelectEvent = (event: EasyTech.Time) => {
    if(onEventClick) onEventClick(event);
    if (event.date_from) setTimeState(new Date(event.date_from));
  };
  /***/

  /**
  * startAccessor: get the start date of an event
  * @param event - the event item 
  */
  const startAccessor = (event: EasyTech.Time) => {
    // Use real dates only when both real start and real end are filled
    if (hasCompletedRealDates(event)) {
      return new Date(event.workOrder!.time!.start);
    }

    return new Date(event.date_from);
  };
  /***/

  /**
  * endAccessor: get the end date of an event
  * @param event - the event item 
  */
  const endAccessor = (event: EasyTech.Time) => {
    // Use real dates only when both real start and real end are filled
    if (hasCompletedRealDates(event)) {
      return new Date(event.workOrder!.time!.end);
    }

    return new Date(event.date_to);
  };
  /***/

  /**
  * titleAccessor: get the title of an event
  * @param event - the event item 
  */
  const titleAccessor = (event: EasyTech.Time) => {
    return event.type.label;
  };
  /***/

  /**
  * houreRangeFormat: format the hour range
  * @param range - the date range
  * @returns - the formatted string 
  */
  const hourRangeFormat = (range: DateRange) => {
    let ret = range.start.getHours().toString().padStart(2, "0")+":";
    ret += range.start.getMinutes().toString().padStart(2, "0")+" - ";
    ret += range.end.getHours().toString().padStart(2, "0")+":";
    ret += range.end.getMinutes().toString().padStart(2, "0");
    return ret;
  };
  /***/

  /**
  * getEventColor: get the color of an event
  * @param event - the event item
  * @returns - the color string 
  */
  const getEventColor = (event: EasyTech.Time) => {
    if (event.technician) {
      if (event.technician.attributes) {
        if (event.workOrder)
          return color.getColor(event.technician.attributes.color, "500");
        else
          return color.getColor(event.technician.attributes.color, "300");
      } else {
        if (event.workOrder) return "gray";
        else return "lightgray";
      }
    } else {
      return "grey";
    }
  };
  /***/

  
  /**
  * getEventColor: get the color of an event
  * @param event - the event item
  * @returns - the color string 
  */
  const getEventBgColor = (event: EasyTech.Time) => {
    if (event.technician) {
      if (event.technician.attributes) {
        if (event.workOrder)
          return color.getColor(event.technician.attributes.color, "100");
        else
          return color.getColor(event.technician.attributes.color, "50");
      } else {
        if (event.workOrder) return "lightgray";
        else return "whitesmoke";
      }
    } else {
      return "grey";
    }
  };
  /***/

  /**
  * useEffect: Get times from the API 
  */
  useEffect(() => {
    if(onPlanningNavigate)
      onPlanningNavigate(actualDate, currentView);
  }, [actualDate, currentView, onPlanningNavigate]);
  /***/

  return (
    <div className={`planning-calendar ${className ?? ""}`}>
      <Calendar
        localizer={localizer}
        culture={calendarLocale}
        messages={calendarMessages}
        startAccessor={startAccessor}
        endAccessor={endAccessor}
        titleAccessor={titleAccessor}
        selectable="ignoreEvents"
        views={["month", "week", "work_week", "day"]}
        defaultView="week"
        onNavigate={onNavigate}
        onView={onView}
        formats={{
          weekdayFormat: "dddd",
          dayFormat: "DD dddd",
          timeGutterFormat: "HH:mm",
          dateFormat: "DD",
          eventTimeRangeFormat: hourRangeFormat,
          selectRangeFormat: hourRangeFormat
        }}
        scrollToTime={timeState}
        step={30}
        timeslots={1}
        onSelectSlot={onSelectTimeSlot}
        onSelectEvent={onSelectEvent}
        eventPropGetter={(event) => {
          const eventColor = getEventColor(event);
          const isWorkorder = Boolean(event.workOrder);
          const isCompletedRealEvent = hasCompletedRealDates(event);
          const isScheduledWorkorder = isWorkorder && !isCompletedRealEvent;

          return {
            className: isCompletedRealEvent
              ? "planning-event-real"
              : isScheduledWorkorder
                ? "planning-event-scheduled"
                : "",
            style: {
              border: "1px solid " + eventColor,
              borderLeft: isWorkorder ? "0" : "5px solid " + eventColor,
              backgroundColor: getEventBgColor(event),
              "--event-accent-color": eventColor
            }
          };
        }}
        dayPropGetter={(date) => {
          return date.getDay() === 6 || date.getDay() === 0
            ? { style: { backgroundColor: "rgb(243 244 246)" } }
            : {};
        }}
        components={{
          event: Event,
          toolbar: (props) =>
            CalendarToolBar({
              loading,
              unasignedWorkordersLength,
              unscheduledWorkordersLength,
              drawerType,
              setDrawerType,
              filterDrawerComponent,
              filterBadgesComponent,
              ...props
            }),
          header: Header
        }}
        events={times}
      />
    </div>
  );
}
/***/
